import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import {
  LockIcon,
  AffordableSvg,
  BalancedSvg,
  QualitySvg,
} from "@/assets/AiBotAssets";
import {
  AUTO_MODEL,
  categoryIcons,
  models,
  MAESTRO_AUTO_MODE_OPTIONS,
  MaestroAutoMode,
  normalizeAutoMode,
  getAutoModeModelName,
  CustomAIConfig,
  EnhancedModel,
  normalize,
  detectProvider,
  providerIcons,
  autoIcon,
  providerIcon,
  UnifiedModelSelectorProps,
} from "../AiBotData";
import { State } from "@/redux/Types";
import { useUserDetails } from "@/react-query/useUserDetails";
import Lottie from "react-lottie";
import animationData from "../../../assets/LottiFiles/CrossAnimation.json";
import "@/pages/onboarding/onboarding.css";
import useGetSingleCompany from "../react-query/useGetSingleCompany";

/* ─── Types ─────────────────────────────────────────────────────────────── */

/* ─── Sidebar navigation (built-in providers + custom entry) ────────────── */
const defaultOptions = {
  loop: false,
  autoplay: true,
  animationData,
  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
};

const BUILTIN_PROVIDERS = [
  // { id: "all", label: "All Models", icon: autoIcon },
  { id: "auto", label: "Auto", icon: autoIcon },
  { id: "openai", label: "OpenAI", icon: providerIcons.openai },
  { id: "anthropic", label: "Anthropic", icon: providerIcons.anthropic },
  { id: "google", label: "Google", icon: providerIcons.google },
  { id: "grok", label: "Grok", icon: providerIcons.grok },
  { id: "z-ai", label: "Z.ai", icon: providerIcons["z-ai"] },
  { id: "minimax", label: "MiniMax", icon: providerIcons.minimax },
  { id: "moonshotai", label: "MoonshotAI", icon: providerIcons.moonshotai },
  { id: "deepseek", label: "DeepSeek", icon: providerIcons.deepseek },
  { id: "groq", label: "Groq", icon: providerIcons.groq },
];

/* Providers shown in the MIDDLE panel of the Custom tab */
const CUSTOM_PROVIDERS = [
  { id: "openai", label: "OpenAI", icon: providerIcons.openai },
  { id: "anthropic", label: "Anthropic", icon: providerIcons.anthropic },
  { id: "google", label: "Google", icon: providerIcons.google },
  // { id: "deepseek", label: "DeepSeek", icon: providerIcons.deepseek },
  { id: "openrouter", label: "OpenRouter", icon: providerIcons.openrouter },
  { id: "groq", label: "Groq", icon: providerIcons.groq },
];

const AUTO_MODE_ICON_BY_ID: Record<MaestroAutoMode, React.FC> = {
  lowCost: AffordableSvg,
  balanced: BalancedSvg,
  quality: QualitySvg,
};

const AUTO_MODE_OPTIONS = MAESTRO_AUTO_MODE_OPTIONS.map((option) => ({
  id: option.value,
  label: option.label,
  categories: option.categories,
  icon: AUTO_MODE_ICON_BY_ID[option.value],
}));

const AUTO_MODE_CATEGORY_ICON_BY_LABEL: Record<string, React.FC | undefined> = {
  "Cost Saver": AffordableSvg,
  Balanced: BalancedSvg,
  Quality: categoryIcons.Quality,
  Affordable: categoryIcons.Affordable,
  Reliable: categoryIcons.Reliable,
  Basic: categoryIcons.Basic,
  Actions: categoryIcons.Actions,
  Consistent: categoryIcons.Consistent,
  Fast: categoryIcons.Fast,
};

/** Same `custom-${id}` as the Custom Keys sidebar (see key row onClick). */
function customProviderIdFromConfigProvider(
  provider: string | undefined,
): string {
  let pid = (provider || "").toLowerCase();
  if (pid.includes("google") || pid.includes("gemini")) pid = "google";
  else if (pid.includes("minimax")) pid = "minimax";
  else if (pid.includes("openai") || pid.includes("gpt")) pid = "openai";
  else if (pid.includes("anthropic") || pid.includes("claude"))
    pid = "anthropic";
  else if (pid.includes("deepseek")) pid = "deepseek";
  else if (pid.includes("openrouter")) pid = "openrouter";
  else if (pid.includes("groq")) pid = "groq";
  return `custom-${pid}`;
}

function isOpenAICompatibleProvider(provider?: string): boolean {
  const value = (provider || "").toLowerCase().trim();
  const compactValue = value.replace(/[\s_-]/g, "");
  return value.includes("custom-model");
}

/** Mongo/API ids may be strings or objects — strict === breaks find() and highlights. */
function idsEqual(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function normalizeIdValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "object") {
    const nestedId = (value as { _id?: unknown })._id;
    if (nestedId != null) return String(nestedId);
  }
  return String(value);
}

/* ─── Micro icons ───────────────────────────────────────────────────────── */
const ChevronDown = ({ rotate = false }) => (
  <svg
    className={`h-[5px] w-[10px] transition-transform ${rotate ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 11 6"
    fill="none"
  >
    <path
      d="M0.583008 0.291992L5.79134 5.70866L10.9997 0.291992L0.583008 0.291992Z"
      fill="#6D6D6D"
    />
  </svg>
);

const KeyIconSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const TrashIconSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);
const BackIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 12L6 8L10 4"
      stroke="#FB5F66"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.9987 14.6615C11.6654 14.6615 14.6654 11.6615 14.6654 7.99479C14.6654 4.32812 11.6654 1.32812 7.9987 1.32812C4.33203 1.32812 1.33203 4.32812 1.33203 7.99479C1.33203 11.6615 4.33203 14.6615 7.9987 14.6615Z"
      stroke="#FB5F66"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.33594 8H10.6693"
      stroke="#FB5F66"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.0026 5.33331V10.6666"
      stroke="#FB5F66"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M10.9412 12.5C11.3468 12.5 11.7358 12.658 12.0226 12.9393C12.3095 13.2206 12.4706 13.6022 12.4706 14C12.4706 13.6022 12.6317 13.2206 12.9185 12.9393C13.2054 12.658 13.5944 12.5 14 12.5C13.5944 12.5 13.2054 12.342 12.9185 12.0607C12.6317 11.7794 12.4706 11.3978 12.4706 11C12.4706 11.3978 12.3095 11.7794 12.0226 12.0607C11.7358 12.342 11.3468 12.5 10.9412 12.5ZM10.9412 3.5C11.3468 3.5 11.7358 3.65804 12.0226 3.93934C12.3095 4.22064 12.4706 4.60218 12.4706 5C12.4706 4.60218 12.6317 4.22064 12.9185 3.93934C13.2054 3.65804 13.5944 3.5 14 3.5C13.5944 3.5 13.2054 3.34196 12.9185 3.06066C12.6317 2.77936 12.4706 2.39782 12.4706 2C12.4706 2.39782 12.3095 2.77936 12.0226 3.06066C11.7358 3.34196 11.3468 3.5 10.9412 3.5ZM5.58824 12.5C5.58824 11.3065 6.07164 10.1619 6.9321 9.31802C7.79256 8.47411 8.9596 8 10.1765 8C8.9596 8 7.79256 7.52589 6.9321 6.68198C6.07164 5.83807 5.58824 4.69347 5.58824 3.5C5.58824 4.69347 5.10483 5.83807 4.24437 6.68198C3.38391 7.52589 2.21688 8 1 8C2.21688 8 3.38391 8.47411 4.24437 9.31802C5.10483 10.1619 5.58824 11.3065 5.58824 12.5Z"
      stroke="#FB5F66"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 1.6C3.36348 1.6 2.75303 1.85286 2.30294 2.30294C1.85286 2.75303 1.6 3.36348 1.6 4V12C1.6 12.6365 1.85286 13.247 2.30294 13.6971C2.75303 14.1471 3.36348 14.4 4 14.4H12C12.6365 14.4 13.247 14.1471 13.6971 13.6971C14.1471 13.247 14.4 12.6365 14.4 12V7.2C14.4 6.98783 14.4843 6.78434 14.6343 6.63432C14.7843 6.48429 14.9878 6.4 15.2 6.4C15.4122 6.4 15.6157 6.48429 15.7657 6.63432C15.9157 6.78434 16 6.98783 16 7.2V12C16 13.0609 15.5786 14.0783 14.8284 14.8284C14.0783 15.5786 13.0609 16 12 16H4C2.93913 16 1.92172 15.5786 1.17157 14.8284C0.421427 14.0783 0 13.0609 0 12V4C0 2.93913 0.421427 1.92172 1.17157 1.17157C1.92172 0.421427 2.93913 0 4 0H8.8C9.01217 0 9.21566 0.0842854 9.36569 0.234315C9.51571 0.384344 9.6 0.587827 9.6 0.8C9.6 1.01217 9.51571 1.21566 9.36569 1.36569C9.21566 1.51571 9.01217 1.6 8.8 1.6H4Z"
      fill="#b3b3b3"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.1724 0.656008C12.5952 0.246992 13.1619 0.0205374 13.7502 0.0254695C14.3385 0.0304017 14.9012 0.266326 15.3172 0.682375C15.7331 1.09842 15.9689 1.66127 15.9737 2.24956C15.9785 2.83785 15.7519 3.40445 15.3428 3.82721L14.7164 4.45441C14.5663 4.60439 14.3629 4.68864 14.1508 4.68864C13.9386 4.68864 13.7352 4.60439 13.5852 4.45441L11.5452 2.41441C11.3952 2.26439 11.3109 2.06094 11.3109 1.84881C11.3109 1.63668 11.3952 1.43323 11.5452 1.28321L12.1724 0.656008ZM10.414 3.54561C10.2639 3.39563 10.0605 3.31138 9.84836 3.31138C9.63623 3.31138 9.43279 3.39563 9.28276 3.54561L5.71396 7.11521C5.61152 7.21761 5.5388 7.34591 5.50356 7.48641L4.82356 10.2064C4.79015 10.3404 4.79201 10.4808 4.82895 10.6139C4.86589 10.747 4.93665 10.8682 5.03435 10.9658C5.13206 11.0635 5.25337 11.1341 5.38648 11.171C5.5196 11.2078 5.65998 11.2095 5.79396 11.176L8.51316 10.496C8.65366 10.4608 8.78196 10.3881 8.88436 10.2856L12.454 6.71681C12.6039 6.56679 12.6882 6.36334 12.6882 6.15121C12.6882 5.93908 12.6039 5.73563 12.454 5.58561L10.414 3.54561Z"
      fill="#b3b3b3"
    />
  </svg>
);
const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="13"
    viewBox="0 0 14 16"
    fill="none"
  >
    <path
      d="M12.0909 4.36341C11.898 4.36341 11.713 4.44003 11.5766 4.57642C11.4402 4.7128 11.3636 4.89778 11.3636 5.09065V13.2291C11.3428 13.5969 11.1774 13.9415 10.9036 14.1879C10.6298 14.4343 10.2698 14.5626 9.90187 14.5447H4.09853C3.73062 14.5626 3.37055 14.4343 3.09677 14.1879C2.82298 13.9415 2.65765 13.5969 2.63679 13.2291V5.09065C2.63679 4.89778 2.56017 4.7128 2.42378 4.57642C2.2874 4.44003 2.10242 4.36341 1.90955 4.36341C1.71667 4.36341 1.5317 4.44003 1.39532 4.57642C1.25893 4.7128 1.18231 4.89778 1.18231 5.09065V13.2291C1.20307 13.9827 1.52163 14.6974 2.06822 15.2166C2.61481 15.7358 3.34488 16.0172 4.09853 15.9992H9.90187C10.6555 16.0172 11.3856 15.7358 11.9322 15.2166C12.4788 14.6974 12.7973 13.9827 12.8181 13.2291V5.09065C12.8181 4.89778 12.7415 4.7128 12.6051 4.57642C12.4687 4.44003 12.2837 4.36341 12.0909 4.36341ZM12.8181 2.18171H9.90914V0.727236C9.90914 0.534361 9.83253 0.349386 9.69614 0.213002C9.55976 0.0766192 9.37478 0 9.18191 0H4.81849C4.62562 0 4.44064 0.0766192 4.30426 0.213002C4.16788 0.349386 4.09126 0.534361 4.09126 0.727236V2.18171H1.18231C0.989439 2.18171 0.804464 2.25833 0.668081 2.39471C0.531697 2.53109 0.455078 2.71607 0.455078 2.90894C0.455078 3.10182 0.531697 3.28679 0.668081 3.42318C0.804464 3.55956 0.989439 3.63618 1.18231 3.63618H12.8181C13.011 3.63618 13.1959 3.55956 13.3323 3.42318C13.4687 3.28679 13.5453 3.10182 13.5453 2.90894C13.5453 2.71607 13.4687 2.53109 13.3323 2.39471C13.1959 2.25833 13.011 2.18171 12.8181 2.18171ZM5.54573 2.18171V1.45447H8.45467V2.18171H5.54573Z"
      fill="#FB5F66"
    />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
      stroke="#5CCF3D"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const CloseIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 1L1 9M1 1L9 9"
      stroke="#aaa"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ─── Marquee name ───────────────────────────────────────────────────────── */
const MarqueeName = ({
  text,
  children,
  className,
  wrapClassName,
}: {
  text?: string;
  children?: React.ReactNode;
  className?: string;
  wrapClassName?: string;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current && innerRef.current) {
        const overflow =
          innerRef.current.scrollWidth - wrapRef.current.clientWidth;
        setOverflowPx(overflow > 0 ? overflow : 0);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [text, children]);

  return (
    <div ref={wrapRef} className={`ums-name-wrap ${wrapClassName || ""}`}>
      <span
        ref={innerRef}
        className={`ums-name-inner ${overflowPx > 0 ? "ums-overflowing" : ""} ${className || ""}`}
        style={{ "--ums-overflow": `-${overflowPx}px` } as React.CSSProperties}
      >
        {children ?? text}
      </span>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
const UnifiedModelSelector = ({
  selectedModel,
  isCustomModel,
  configs,
  botData,
  byok,
  defaultByokConfigId,
  allowedModels,
  plan,
  handleModelSelect,
  handleConfigSelect,
  handleModelSourceChange,
  onAddNewConfig,
  onEditConfig,
  onDeleteConfig,
  onAddModelToConfig,
  onDirectModelUpdate,
  onUpdateConfig,
  onMaestroModeChange,
  onTryNow,
  hideCategories,
  hideModelQualities = false,
  portalMinWidth,
  closeOnSelect,
  hideApiKeyOption = false,
  readOnly = false,
  canManageProviderKeys = true,
}: UnifiedModelSelectorProps) => {
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const isNewUser = useSelector((state: State) => state.user.isNewUser);
  const { data: userDetails } = useUserDetails();
  const isBasicOrProOrTrialPlan = plan?.name?.toLowerCase?.() !== "lifetime";
  const isByokVisibleUser = !fromOutgrow && !!isNewUser;
  const useByokHiddenFallback = !isByokVisibleUser;
  const shouldBlockDefaultModelSelection =
    plan?.name?.toLowerCase() === "lifetime" &&
    !isNewUser &&
    (configs?.length ?? 0) === 0;
  const canMutateProviderKeys = canManageProviderKeys && !readOnly;
  const showByokCta =
    canMutateProviderKeys &&
    !hideApiKeyOption &&
    !byok &&
    !fromOutgrow &&
    !!isNewUser;
  const [open, setOpen] = useState(false);
  const [isSwitchingSource, setIsSwitchingSource] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [isManualActiveProviderChanged, setIsManualActiveProviderChanged] =
    useState(false);

  const rawConfigId = botData?.aiBotConfig?.configId;
  const selectedConfigId = normalizeIdValue(rawConfigId);
  const selectedConfig = configs?.find((c) =>
    selectedConfigId != null ? idsEqual(c._id, selectedConfigId) : false,
  );

  // Left sidebar: which main tab is active
  const [activeProvider, setActiveProvider] = useState<string>(
    selectedConfig && !hideApiKeyOption
      ? "custom-all"
      : botData?.botModel
        ? "default-all"
        : "default-auto",
  );

  // Which config is shown in detail
  const [detailConfigId, setDetailConfigId] = useState<string | null>(null);
  // Built-in: hovered model for right preview
  const [hoveredModel, setHoveredModel] = useState<EnhancedModel | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const modelListRef = useRef<HTMLDivElement>(null);
  const [isModelListScrollable, setIsModelListScrollable] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });
  const lastOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setIsModelListScrollable(false);
      return;
    }

    const list = modelListRef.current;
    if (!list) return;

    const updateScrollableState = () => {
      setIsModelListScrollable(list.scrollHeight > list.clientHeight + 1);
    };

    updateScrollableState();
    const resizeObserver = new ResizeObserver(updateScrollableState);
    resizeObserver.observe(list);
    return () => resizeObserver.disconnect();
  }, [open, activeProvider, detailConfigId, configs, botData?.botModel]);

  const allModels: EnhancedModel[] = [AUTO_MODEL, ...models];

  const resolvedPersistedModel = botData?.botModel || null;

  const getModelDisplayName = (mName: string | undefined | null) => {
    if (!mName) return "";
    const norm = normalize(mName);
    const found = allModels.find((m) => normalize(m.name) === norm);
    if (found) return found.displayName;
    // Strip prefixes for a cleaner look even if not found in allModels
    return mName
      .replace(
        /^(openai\/|anthropic\/|google\/|groq\/|deepseek\/|grok\/|z-ai\/)/,
        "",
      )
      .replace(/^(gpt-|claude-|gemini-)/, "");
  };

  // Close modal on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Calculate dropdown position when opening
  const updateDropdownPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      let finalWidth = rect.width;
      if (portalMinWidth && rect.width < portalMinWidth) {
        finalWidth = portalMinWidth;
      }

      let finalLeft = rect.left + window.scrollX;
      // Adjust left if the popup overflows the screen on the right
      if (finalLeft + finalWidth > window.innerWidth - 8) {
        finalLeft = window.innerWidth - finalWidth - 8;
        if (finalLeft < 8) finalLeft = 8;
      }

      const newTop = rect.bottom + 6 + window.scrollY;

      setDropdownPos((prev) => {
        if (
          prev.top === newTop &&
          prev.left === finalLeft &&
          prev.width === finalWidth
        ) {
          return prev;
        }
        return {
          top: newTop,
          left: finalLeft,
          width: finalWidth,
        };
      });
    }
  }, [portalMinWidth]);

  useEffect(() => {
    if (!open) return;
    updateDropdownPos();

    const handleScroll = (e: Event) => {
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleBlur = () => {
      setOpen(false);
    };

    window.addEventListener("resize", updateDropdownPos);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("resize", updateDropdownPos);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("blur", handleBlur);
    };
  }, [open, updateDropdownPos]);

  useEffect(() => {
    if (!shouldBlockDefaultModelSelection) return;
    setActiveProvider("custom-all");
    setDetailConfigId(null);
    setHoveredModel(null);
  }, [shouldBlockDefaultModelSelection]);

  // When opening, automatically land on the active provider and expand its key
  useEffect(() => {
    if (open && !isSwitchingSource && !isManualActiveProviderChanged) {
      const isCurrentlyLanding = !lastOpenRef.current;

      if (shouldBlockDefaultModelSelection) {
        if (
          isCurrentlyLanding ||
          activeProvider.startsWith("default-") ||
          activeProvider === "default-all"
        ) {
          setActiveProvider("custom-all");
          setDetailConfigId(null);
        }
      } else if (isCustomModel && selectedConfig) {
        let p = (selectedConfig.provider || "").toLowerCase();
        if (p.includes("google") || p.includes("gemini")) p = "google";
        else if (p.includes("minimax")) p = "minimax";
        else if (p.includes("openai") || p.includes("gpt")) p = "openai";
        else if (p.includes("anthropic") || p.includes("claude"))
          p = "anthropic";
        else if (p.includes("deepseek")) p = "deepseek";
        else if (p.includes("openrouter")) p = "openrouter";
        else if (p.includes("groq")) p = "groq";

        const target = `custom-${p}`;
        if (activeProvider !== target || !detailConfigId) {
          if (
            isCurrentlyLanding ||
            !activeProvider.startsWith("custom-") ||
            activeProvider === "custom-all" ||
            !detailConfigId
          ) {
            setActiveProvider(target);
            setDetailConfigId(normalizeIdValue(selectedConfig._id));
          }
        }
      } else if (!isCustomModel && selectedModel) {
        // Important: ignoreConfig=true as Default mode should ignore stale BYOK configs
        const p = detectProvider(selectedModel, true);
        const target = `default-${p}`;

        if (activeProvider !== target) {
          if (
            isCurrentlyLanding ||
            activeProvider === "default-all" ||
            activeProvider.startsWith("custom-")
          ) {
            setActiveProvider(target);
          }
        }
      }
    }

    if (open) {
      lastOpenRef.current = true;
    } else {
      lastOpenRef.current = false;
      setIsManualActiveProviderChanged(false);
    }
  }, [
    open,
    isCustomModel,
    selectedConfig,
    selectedModel,
    isSwitchingSource,
    activeProvider,
    detailConfigId,
    shouldBlockDefaultModelSelection,
  ]);

  // Built-in models filtered by sidebar
  const filteredModels =
    activeProvider === "default-all" || activeProvider === "all"
      ? allModels.filter((m) => m.name !== "auto") // Exclude auto from 'All Models'
      : allModels.filter(
          (m) => `default-${detectProvider(m)}` === activeProvider,
        );

  const handleSidebarClick = (id: string) => {
    setActiveProvider(id);
    setIsManualActiveProviderChanged(true);
    setHoveredModel(null);
    setDetailConfigId(null);
    setIsEditingName(false);

    // Only switch source (and thus update bot model) if moving between Custom and Default
    if (id.startsWith("custom-") && isCustomModel) {
      setIsSwitchingSource(true);
      handleModelSourceChange("custom");
      setTimeout(() => setIsSwitchingSource(false), 500);
    } else if (id.startsWith("default-") && !isCustomModel) {
      setIsSwitchingSource(true);
      handleModelSourceChange("main", id.replace("default-", ""));
      setTimeout(() => setIsSwitchingSource(false), 500);
    }
  };

  const handleSaveName = (configId: string) => {
    if (onUpdateConfig && editingName.trim()) {
      onUpdateConfig(configId, { name: editingName.trim() });
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditingName("");
  };

  // Get configs per provider
  const configsForProvider = (pid: string) =>
    pid === "all"
      ? configs
      : configs.filter((c) => {
          const p = (c.provider || "").toLowerCase();
          if (pid === "google")
            return p.includes("google") || p.includes("gemini");
          if (pid === "openai")
            return p.includes("openai") || p.includes("gpt");
          if (pid === "anthropic")
            return p.includes("anthropic") || p.includes("claude");
          if (pid === "deepseek") return p.includes("deepseek");
          if (pid === "openrouter")
            return p.includes("openrouter") || p.includes("open router");

          // Fallback catch-all for groq / others (matches detectProvider behavior)
          if (pid === "groq") {
            return (
              !p.includes("google") &&
              !p.includes("gemini") &&
              !p.includes("openai") &&
              !p.includes("gpt") &&
              !p.includes("anthropic") &&
              !p.includes("claude") &&
              !p.includes("deepseek") &&
              !p.includes("openrouter") &&
              !p.includes("open router")
            );
          }
          return p === pid;
        });

  const customKeysUiActive =
    !hideApiKeyOption && (selectedConfig || useByokHiddenFallback);
  const soleCustomConfig =
    customKeysUiActive && configs?.length === 1 ? configs[0] : null;
  const activeCustomProvider =
    activeProvider.startsWith("custom-") && activeProvider !== "custom-all"
      ? activeProvider
      : null;
  const configsForActiveCustomProvider = activeCustomProvider
    ? (configs ?? []).filter(
        (config) =>
          customProviderIdFromConfigProvider(config.provider) ===
          activeCustomProvider,
      )
    : [];
  /** Which key drives the right-hand model list (detail focus → bot selection → only key). */
  const resolvedPanelCustomConfig =
    customKeysUiActive && activeCustomProvider
      ? (detailConfigId &&
          configsForActiveCustomProvider.find((c) =>
            idsEqual(c._id, detailConfigId),
          )) ||
        (selectedConfigId &&
          configsForActiveCustomProvider.find((c) =>
            idsEqual(c._id, selectedConfigId),
          )) ||
        (configsForActiveCustomProvider.length === 1
          ? configsForActiveCustomProvider[0]
          : null) ||
        (soleCustomConfig &&
        customProviderIdFromConfigProvider(soleCustomConfig.provider) ===
          activeCustomProvider
          ? soleCustomConfig
          : null) ||
        null
      : null;

  const showCustomSelectKeyPlaceholder =
    customKeysUiActive &&
    activeProvider.startsWith("custom-") &&
    !resolvedPanelCustomConfig;
  const shouldShowDefaultModelsSection =
    hideApiKeyOption ||
    (plan?.name !== "Lifetime" &&
      useByokHiddenFallback &&
      (isBasicOrProOrTrialPlan ||
        plan?.name === "Professional" ||
        (!isNewUser && !userDetails?.appSumo)));
  const showNoCustomKeysModelFallback =
    customKeysUiActive &&
    !shouldShowDefaultModelsSection &&
    (configs?.length ?? 0) === 0;
  useEffect(() => {
    if (!showNoCustomKeysModelFallback) return;
    setActiveProvider("custom-all");
    setDetailConfigId(null);
    setHoveredModel(null);
  }, [showNoCustomKeysModelFallback]);

  /* ── Trigger display ─────────────────────────────────────────────────── */
  const persistedConfigId = normalizeIdValue(botData?.aiBotConfig?.configId);
  const hasMissingPersistedConfigId = persistedConfigId
    ? !(configs ?? []).some((config) =>
        idsEqual(config?._id, persistedConfigId),
      )
    : true;
  const hasNoResolvedModel =
    !selectedConfig && !selectedModel && !resolvedPersistedModel;

  const noCustomSelected =
    !hideApiKeyOption &&
    ((isCustomModel && !selectedConfig) ||
      showNoCustomKeysModelFallback ||
      shouldBlockDefaultModelSelection ||
      (hasMissingPersistedConfigId &&
        plan?.name === "Lifetime" &&
        !isNewUser) ||
      hasNoResolvedModel);
  const shouldShowCustomKeyPlaceholder =
    activeProvider.startsWith("custom-") &&
    (showCustomSelectKeyPlaceholder ||
      (noCustomSelected && !showNoCustomKeysModelFallback));
  const selectedCustomProviderId = selectedConfig?.provider
    ? customProviderIdFromConfigProvider(selectedConfig.provider)
    : null;
  const selectedCustomProviderLabel =
    CUSTOM_PROVIDERS.find(
      (provider) => `custom-${provider.id}` === selectedCustomProviderId,
    )?.label || null;
  const triggerIcon = noCustomSelected
    ? autoIcon
    : isCustomModel
      ? providerIcon(selectedConfig?.provider)
      : selectedModel?.url || autoIcon;
  const selectedAutoMode = normalizeAutoMode(
    botData?.aiBotConfig?.maestroFlowConfig?.selectedMode ||
      botData?.maestroFlowConfig?.selectedMode,
  );

  const defaultModelProviderLabel = (() => {
    if (!selectedModel) return null;
    const p = detectProvider(selectedModel, true);
    return (
      BUILTIN_PROVIDERS.find((pr) => pr.id === p)?.label ||
      (p ? p.charAt(0).toUpperCase() + p.slice(1) : null)
    );
  })();
  const selectedCustomModelName = isCustomModel
    ? botData?.aiBotConfig?.model ||
      botData?.botModel ||
      selectedModel?.name ||
      selectedConfig?.model ||
      ""
    : "";
  const selectedCustomModelLabel = selectedCustomModelName
    ? getModelDisplayName(selectedCustomModelName) || selectedCustomModelName
    : "";
  // triggerName = top line, triggerSub = bottom line
  const triggerName = noCustomSelected
    ? isCustomModel
      ? selectedCustomProviderLabel || "Select Provider"
      : "Select AI Model"
    : isCustomModel
      ? selectedConfig?.name || "No key selected"
      : selectedModel?.name === "auto"
        ? `Auto - ${MAESTRO_AUTO_MODE_OPTIONS.find((o) => o.value === selectedAutoMode)?.label || "Balanced"}`
        : defaultModelProviderLabel || "Select AI Model";
  const triggerModelName = isCustomModel
    ? selectedCustomModelName
    : selectedModel?.name || "";
  const isAutoSelected =
    triggerModelName === "auto" || triggerModelName.startsWith("auto-");
  const autoLabel = `Auto - ${MAESTRO_AUTO_MODE_OPTIONS.find((o) => o.value === selectedAutoMode)?.label || "Balanced"}`;
  const triggerSub = noCustomSelected
    ? "No model selected"
    : isCustomModel
      ? isAutoSelected
        ? autoLabel
        : selectedCustomModelLabel ||
          selectedModel?.displayName ||
          selectedCustomProviderLabel ||
          "No model selected"
      : isAutoSelected
        ? ""
        : selectedModel?.displayName || "No model selected";

  /* ── Left sidebar list ───────────────────────────────────────────────── */
  const renderSidebarItem = (
    p: { id: string; label: string; icon: any },
    isCustom: boolean,
  ) => {
    const providerId = p.id === "all" ? "default-all" : `default-${p.id}`;
    const isSel = activeProvider === providerId;
    const shouldLockDefaultInByok = false;
    const shouldLockCustomInDefault = !byok && isCustom && !selectedConfigId;
    const isLocked = shouldLockDefaultInByok || shouldLockCustomInDefault;
    return (
      <button
        key={p.id}
        disabled={isLocked}
        onClick={() => !isLocked && handleSidebarClick(providerId)}
        className={`flex items-center gap-[8px] rounded-[8px] px-[10px] py-[8px] text-left text-[12px] font-medium transition-colors ${
          isSel
            ? fromOutgrow
              ? "bg-[#E8F0FE] text-[#1A73E8]"
              : "bg-[#FFEAEB] text-[#FB5F66]"
            : ""
        } ${
          isLocked
            ? "cursor-not-allowed opacity-30"
            : !isSel
              ? "text-[#666] hover:bg-[#f3f3f3] hover:text-[#333]"
              : ""
        } ${isCustom && byok ? (fromOutgrow ? "ring-1 ring-[#1A73E840]" : "ring-1 ring-[#FB5F6640]") : ""} ${fromOutgrow ? (isSel ? "unified-outgrow-active" : isLocked ? "" : "unified-outgrow-hover") : ""}`}
      >
        {isCustom ? (
          <span
            className={`flex h-[16px] w-[16px] flex-shrink-0 items-center justify-center ${isSel ? (fromOutgrow ? "text-[#1A73E8]" : "text-[#FB5F66]") : "text-[#999]"}`}
          >
            <KeyIconSvg />
          </span>
        ) : (
          <img
            src={p.icon}
            alt={p.label}
            className="h-[16px] w-[16px] flex-shrink-0 rounded-sm"
          />
        )}
        <span className="truncate">{p.label}</span>
        {isLocked && (
          <span className="ml-auto text-[#ccc]">
            <LockIcon />
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="relative flex flex-col gap-[10px]" ref={containerRef}>
      <style>{`
        @keyframes ums-marquee {
          0%        { transform: translateX(0); }
          10%       { transform: translateX(0); }
          90%       { transform: translateX(var(--ums-overflow)); }
          100%      { transform: translateX(var(--ums-overflow)); }
        }
        .ums-name-wrap {
          overflow: hidden;
          flex: 1;
          min-width: 0;
        }
        .ums-name-inner {
          display: inline-block;
          white-space: nowrap;
          transition: none;
        }
        .group:hover .ums-name-inner.ums-overflowing {
          animation: ums-marquee 3s ease-in-out infinite alternate;
        }
      `}</style>
      {fromOutgrow && (
        <style>{`
          .unified-model-selector-outgrow .unified-outgrow-hover:hover {
            background: #eaf4fb !important;
            color: #444 !important;
            border-color: #3e9adc !important;
          }
          .unified-model-selector-outgrow .unified-outgrow-active {
            background: #eaf4fb !important;
            color: #444 !important;
            border-color: #3e9adc00 !important;
          }
          .unified-model-selector-outgrow  .models-container .unified-outgrow-active{
                        box-shadow: 0 0 5px 0 rgba(62, 154, 220, 0.9) !important;
                        background: #fff !important;

            }
          .unified-model-selector-outgrow .unified-outgrow-hover:hover span,
          .unified-model-selector-outgrow .unified-outgrow-hover:hover p,
          .unified-model-selector-outgrow .unified-outgrow-active span,
          .unified-model-selector-outgrow .unified-outgrow-active p {
            color: #444 !important;
          }
          // .unified-model-selector-outgrow .unified-outgrow-hover:hover svg path,
          // .unified-model-selector-outgrow .unified-outgrow-active svg path {
          //   fill: #ffffff !important;
          //   stroke: #ffffff !important;
          // }
        `}</style>
      )}
      {!hideCategories && (
        <h3 className="text-[14px] font-medium text-[#333]">AI Model</h3>
      )}

      <button
        className={`group flex min-w-0 items-center justify-between gap-[10px] rounded-[10px] border border-[rgba(0,0,0,0.10)] bg-white transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] ${hideCategories ? "w-full max-w-[268px] p-[12px]" : "w-full px-[15px] py-[13px]"}`}
        onClick={() => !readOnly && setOpen((v) => !v)}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[10px]">
          <img
            src={triggerIcon}
            alt="model"
            className={`flex-shrink-0 rounded ${hideCategories ? "h-[13px] w-[13px]" : "h-[20px] w-[20px]"}`}
          />
          <div
            className={`flex min-w-0 flex-1 ${hideCategories ? "flex-row items-center gap-[6px]" : "flex-col items-start"}`}
          >
            <span
              className={`truncate font-medium text-[#333] ${hideCategories ? "text-[12px] leading-[110%]" : "text-[13px] leading-[1.2]"}`}
            >
              {triggerName}
            </span>
            <MarqueeName
              className={`text-[#999] ${hideCategories ? "text-[12px] leading-[110%]" : "text-[11px] leading-[1.2]"}`}
              wrapClassName="max-w-full text-left flex"
            >
              {triggerSub}
            </MarqueeName>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-[8px]">
          {isNewUser && byok && isBasicOrProOrTrialPlan && true && (
            <span className="flex-shrink-0 rounded-full bg-[#FFF7ED] px-[8px] py-[3px] text-[11px] font-medium text-[#d97706]">
              BYOK Active
            </span>
          )}

          <ChevronDown rotate={open} />
        </div>
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className={`fixed z-[9999] flex h-[367px] max-h-[367px] min-h-[310px] flex-col overflow-hidden rounded-[14px] border border-[rgba(0,0,0,0.08)] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] ${fromOutgrow ? "outgrow unified-model-selector-outgrow" : ""}`}
              style={{
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                maxWidth: dropdownPos.width,
              }}
            >
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-[15px]">
                <div className="flex min-h-0 flex-1 overflow-hidden">
                  <div
                    className="default-models-container flex h-full flex-shrink-0 flex-col justify-between border-r border-[rgba(0,0,0,0.07)]"
                    style={{
                      width: window.innerWidth < 1560 ? "180px" : "220px",
                    }}
                  >
                    {/* {byok && (
                  <div className="border-b border-[#f0e4d4] bg-[#fdf4e7] px-[14px] py-[12px]">
                    <div className="mb-[4px] flex items-center gap-[6px]">
                      <svg
                        width="12"
                        height="15"
                        viewBox="0 0 12 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="1"
                          y="6"
                          width="10"
                          height="8"
                          rx="2"
                          fill="#F59E0B"
                        />
                        <path
                          d="M3 6V4C3 2.34315 4.34315 1 6 1C7.65685 1 9 2.34315 9 4V6"
                          stroke="#F59E0B"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <rect
                          x="5.5"
                          y="9"
                          width="1"
                          height="3"
                          rx="0.5"
                          fill="#fdf4e7"
                        />
                      </svg>
                      <p className="text-[12px] font-bold text-[#92400e]">
                        BYOK mode
                      </p>
                    </div>
                    <p className="text-[11px] font-medium text-[#b45309]">
                      Use your own API keys
                    </p>
                  </div>
                )} */}

                    <div className="grey-scrollbar grey-scrollbar flex h-max max-h-[300px] flex-col gap-[2px] overflow-y-auto pr-[15px]">
                      {byok || useByokHiddenFallback ? (
                        <>
                          {shouldShowDefaultModelsSection && (
                            <>
                              <div className="mb-[6px] mt-[4px] px-[10px] text-[11px] uppercase tracking-[0.06em] text-[#666666]">
                                {!fromOutgrow
                                  ? "Default Models"
                                  : "Select Provider"}
                              </div>
                              {BUILTIN_PROVIDERS.map((p) =>
                                renderSidebarItem(p, false),
                              )}
                            </>
                          )}

                          {!hideApiKeyOption && !fromOutgrow && (
                            <>
                              <div className="mb-[6px] mt-[4px] px-[10px] text-[11px] uppercase tracking-[0.06em] text-[#666666]">
                                Custom Keys
                              </div>

                              {(configs?.length ?? 0) === 0 ? (
                                <div className="flex flex-col items-center px-[10px] pb-[10px] pt-[4px] text-center">
                                  {!isBasicOrProOrTrialPlan && true && (
                                    <Lottie
                                      options={defaultOptions}
                                      height={150}
                                      width={150}
                                    />
                                  )}
                                  <p className="mt-[8px] text-[11px] font-medium leading-[1.35] text-[#888]">
                                    {!canMutateProviderKeys
                                      ? "No connected custom providers are available."
                                      : showByokCta
                                        ? "No API keys yet. Use Try Now to enable BYOK and add keys."
                                        : "No API keys yet. Add a key to use your own providers."}
                                  </p>
                                </div>
                              ) : (
                                configs.map((config) => {
                                  const matchesActiveCustomProvider =
                                    !!activeCustomProvider &&
                                    customProviderIdFromConfigProvider(
                                      config.provider,
                                    ) === activeCustomProvider;
                                  const isActiveConfig =
                                    isCustomModel &&
                                    selectedConfigId != null &&
                                    idsEqual(config._id, selectedConfigId);
                                  const isViewing =
                                    matchesActiveCustomProvider &&
                                    !!resolvedPanelCustomConfig &&
                                    idsEqual(
                                      config._id,
                                      resolvedPanelCustomConfig._id,
                                    );
                                  const configProvider = (
                                    config.provider || ""
                                  ).toLowerCase();
                                  const icon = providerIcon(config.provider);
                                  return (
                                    <button
                                      key={
                                        normalizeIdValue(config._id) ??
                                        `${config.provider}-${config.name}`
                                      }
                                      onClick={() => {
                                        let pid = configProvider;
                                        if (
                                          pid.includes("google") ||
                                          pid.includes("gemini")
                                        )
                                          pid = "google";
                                        else if (pid.includes("minimax"))
                                          pid = "minimax";
                                        else if (
                                          pid.includes("openai") ||
                                          pid.includes("gpt")
                                        )
                                          pid = "openai";
                                        else if (
                                          pid.includes("anthropic") ||
                                          pid.includes("claude")
                                        )
                                          pid = "anthropic";
                                        else if (pid.includes("openrouter"))
                                          pid = "openrouter";
                                        else if (pid.includes("groq"))
                                          pid = "groq";
                                        const providerId = `custom-${pid}`;
                                        setActiveProvider(providerId);
                                        setIsManualActiveProviderChanged(true);
                                        setDetailConfigId(
                                          normalizeIdValue(config._id),
                                        );
                                        handleConfigSelect(config._id);
                                      }}
                                      className={`group flex w-full items-center gap-[8px] rounded-[8px] px-[10px] py-[8px] text-left text-[12px] font-medium transition-colors ${
                                        isActiveConfig || isViewing
                                          ? fromOutgrow
                                            ? "bg-[#E8F0FE] text-[#1A73E8]"
                                            : "bg-[#FFEAEB] text-[#FB5F66]"
                                          : "text-[#666] hover:bg-[#f3f3f3] hover:text-[#333]"
                                      } ${fromOutgrow ? (isActiveConfig || isViewing ? "unified-outgrow-active" : "unified-outgrow-hover") : ""}`}
                                    >
                                      <img
                                        src={icon}
                                        alt={config.provider || ""}
                                        className="h-[16px] w-[16px] flex-shrink-0 rounded-sm"
                                      />
                                      <span
                                        className={`flex-1 truncate text-[13px]`}
                                      >
                                        {config.name}
                                      </span>
                                      {/* {canMutateProviderKeys &&
                                        isOpenAICompatibleProvider(
                                          config.provider,
                                        ) &&
                                        onAddModelToConfig && (
                                          <div
                                            className="flex items-center gap-[3px] rounded-[6px] px-[5px] py-[3px] text-[10px] font-medium text-[#FB5F66] opacity-0 transition-opacity hover:bg-[#FFF0F1] group-hover:opacity-100"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onAddModelToConfig(config);
                                              setOpen(false);
                                            }}
                                          >
                                            <PlusIcon /> Model
                                          </div>
                                        )} */}
                                      {canMutateProviderKeys &&
                                        useByokHiddenFallback && (
                                          <div
                                            className="opacity-0 transition-opacity hover:text-[#FB5F66] group-hover:opacity-100"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDeleteConfig(config._id);
                                            }}
                                          >
                                            <TrashIconSvg />
                                          </div>
                                        )}
                                    </button>
                                  );
                                })
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="mb-[6px] mt-[4px] px-[10px] text-[11px] uppercase tracking-[0.06em] text-[#666666]">
                            Default Models
                          </div>
                          {BUILTIN_PROVIDERS.map((p) =>
                            renderSidebarItem(p, false),
                          )}
                        </>
                      )}
                    </div>
                    {canMutateProviderKeys &&
                      !hideApiKeyOption &&
                      !showByokCta &&
                      !fromOutgrow && (
                        <button
                          className="mt-[4px] flex w-[93%] items-center justify-center gap-[4px] rounded-[8px] bg-[#F9F9F9] py-[8px] text-[12px] text-[#FB5F66] hover:opacity-80"
                          onClick={() => {
                            onAddNewConfig();
                          }}
                        >
                          <PlusIcon /> Add new key
                        </button>
                      )}
                  </div>

                  <div className="models-container relative z-10 flex h-full max-h-[335px] min-h-0 flex-1 flex-col overflow-hidden bg-white">
                    <div className="mb-[6px] mt-[4px] px-[10px] text-[11px] uppercase tracking-[0.06em] text-[#666666]">
                      Select a model
                    </div>

                    {showNoCustomKeysModelFallback ? (
                      <div className="flex h-full w-full flex-col items-center justify-center p-[20px] text-center">
                        <div className="mb-[10px] flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#FFF0F1] text-[#FB5F66]">
                          <KeyIconSvg />
                        </div>
                        <p className="mt-[8px] text-[12px] font-medium leading-[1.35] text-[#888]">
                          {canMutateProviderKeys
                            ? "No API keys yet. Add a key to view and switch models."
                            : "No connected models are available."}
                        </p>
                      </div>
                    ) : shouldShowCustomKeyPlaceholder ? (
                      <div className="flex h-full w-full flex-col items-center justify-center p-[20px] text-center">
                        <div className="mb-[10px] flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#FFF0F1] text-[#FB5F66]">
                          <KeyIconSvg />
                        </div>
                        <p className="text-[12px] text-[#888]">
                          Select a key to view and switch models.
                        </p>
                      </div>
                    ) : (
                      (() => {
                        /* ── Resolve which models to show & header info ──────── */
                        console.log("Active provider:", activeProvider);
                        const providerForModelPanel =
                          activeCustomProvider && resolvedPanelCustomConfig
                            ? customProviderIdFromConfigProvider(
                                resolvedPanelCustomConfig.provider,
                              )
                            : activeProvider;
                        const isCustomView =
                          providerForModelPanel.startsWith("custom-") &&
                          providerForModelPanel !== "custom-all";
                        const viewingConfig = isCustomView
                          ? resolvedPanelCustomConfig
                          : null;

                        // For custom view, filter models by the config's provider
                        const displayModels: EnhancedModel[] = isCustomView
                          ? (() => {
                              if (!viewingConfig?.provider) return [];
                              const vp = viewingConfig.provider.toLowerCase();
                              const icon = providerIcon(vp);
                              if (
                                isOpenAICompatibleProvider(
                                  viewingConfig.provider,
                                )
                              ) {
                                const savedModels =
                                  Array.isArray(viewingConfig.models) &&
                                  viewingConfig.models.length > 0
                                    ? viewingConfig.models
                                    : typeof viewingConfig.model === "string" &&
                                        viewingConfig.model.trim()
                                      ? [viewingConfig.model.trim()]
                                      : [];

                                return savedModels.map((modelName) => ({
                                  name: modelName,
                                  displayName: modelName,
                                  url: icon,
                                  provider: viewingConfig.provider,
                                  configId: viewingConfig._id,
                                }));
                              }
                              return models
                                .filter((m) => {
                                  const mp = detectProvider(m, true);
                                  if (
                                    vp.includes("openrouter") ||
                                    vp.includes("open router")
                                  )
                                    return true;
                                  if (mp === "google")
                                    return (
                                      vp.includes("google") ||
                                      vp.includes("gemini")
                                    );
                                  if (mp === "openai")
                                    return (
                                      vp.includes("openai") ||
                                      vp.includes("gpt")
                                    );
                                  if (mp === "anthropic")
                                    return (
                                      vp.includes("anthropic") ||
                                      vp.includes("claude")
                                    );
                                  return mp === vp;
                                })
                                .map((m) => ({
                                  ...m,
                                  url: icon,
                                  configId: viewingConfig._id,
                                }));
                            })()
                          : shouldBlockDefaultModelSelection
                            ? []
                            : filteredModels;

                        // Header label
                        const headerLabel = isCustomView
                          ? isOpenAICompatibleProvider(viewingConfig?.provider)
                            ? `${viewingConfig?.name || "Custom"} Model`
                            : `${viewingConfig?.name || ""} Models`
                          : activeProvider === "default-all" ||
                              activeProvider === "all"
                            ? "All Models"
                            : (BUILTIN_PROVIDERS.find(
                                (p) => `default-${p.id}` === activeProvider,
                              )?.label ?? "");

                        const selectedAutoMode = normalizeAutoMode(
                          botData?.aiBotConfig?.maestroFlowConfig
                            ?.selectedMode ||
                            botData?.maestroFlowConfig?.selectedMode,
                        );
                        const renderModels: EnhancedModel[] =
                          displayModels.flatMap((model) => {
                            if (model.name === "auto") {
                              return AUTO_MODE_OPTIONS.map((mode) => ({
                                ...model,
                                name: `auto-${mode.id}`,
                                displayName: `Auto - ${mode.label}`,
                                categories: [...mode.categories],
                                autoMode: mode.id,
                              }));
                            }
                            return [model];
                          });

                        const getIsSelected = (model: EnhancedModel) => {
                          // Keep selection scoped to the active source.
                          // A custom-key model with the same name should not appear
                          // selected in the default provider tabs, and vice versa.
                          if (isCustomView && !isCustomModel) return false;
                          if (!isCustomView && isCustomModel) return false;

                          const savedModel =
                            selectedModel?.name ||
                            botData?.aiBotConfig?.model ||
                            botData?.botModel ||
                            (isCustomView && viewingConfig
                              ? viewingConfig.model
                              : null);
                          if (!savedModel) return false;

                          if (model.autoMode) {
                            const autoModeModelName = getAutoModeModelName(
                              model.autoMode,
                            );
                            const currentAutoMode =
                              selectedModel?.autoMode || selectedAutoMode;
                            const isAutoMatch =
                              savedModel === autoModeModelName ||
                              ((savedModel === "auto" ||
                                savedModel.startsWith("auto-")) &&
                                currentAutoMode === model.autoMode);

                            // Auto models are universal and shouldn't be blocked by configId checks
                            return isAutoMatch;
                          }

                          // Exact match
                          if (savedModel === model.name) {
                            return isCustomView
                              ? !!model.configId
                              : !model.configId;
                          }

                          // Robust match: normalize both and compare
                          const isMatch =
                            normalize(savedModel) === normalize(model.name);
                          if (isMatch) {
                            return isCustomView
                              ? !!model.configId
                              : !model.configId;
                          }

                          return false;
                        };

                        // Click handler (works for both modes)
                        const handleClick = (model: EnhancedModel) => {
                          if (
                            shouldBlockDefaultModelSelection &&
                            !isCustomView
                          ) {
                            return;
                          }
                          if (isCustomView && viewingConfig) {
                            const updatedConfig = {
                              ...viewingConfig,
                              model: model.autoMode
                                ? getAutoModeModelName(model.autoMode)
                                : model.name,
                            };
                            if (onDirectModelUpdate)
                              onDirectModelUpdate(updatedConfig);
                            else onEditConfig(updatedConfig);
                          } else if (model.autoMode) {
                            onMaestroModeChange?.(model.autoMode);
                            handleModelSelect(AUTO_MODEL);
                          } else {
                            handleModelSelect(model);
                          }
                          if (closeOnSelect) setOpen(false);
                        };

                        return (
                          <>
                            {/* Header */}
                            {/* <div className="flex items-center justify-between border-b border-[rgba(0,0,0,0.06)] px-[14px] py-[10px]">
                          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
                            {isCustomView && viewingConfig && (
                              <img
                                src={providerIcon(viewingConfig.provider)}
                                alt=""
                                className="h-[14px] w-[14px] flex-shrink-0 rounded-sm"
                              />
                            )}
                            {isCustomView && isEditingName ? (
                              <input
                                autoFocus
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && viewingConfig)
                                    handleSaveName(viewingConfig._id);
                                  if (e.key === "Escape") handleCancelEdit();
                                }}
                                className="w-full rounded-[4px] border border-[#FB5F6633] px-[6px] py-[2px] text-[11px] font-semibold text-[#444] outline-none focus:border-[#FB5F66]"
                              />
                            ) : (
                              <p className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-[#aaa]">
                                {headerLabel}
                              </p>
                            )}
                          </div>
                          {isCustomView && viewingConfig && (
                            <div className="ml-[8px] flex gap-[4px]">
                              {isEditingName ? (
                                <>
                                  <button
                                    onClick={() =>
                                      handleSaveName(viewingConfig._id)
                                    }
                                    className="flex h-[24px] w-[24px] items-center justify-center rounded-[6px] hover:bg-[#F0FFF0]"
                                  >
                                    <CheckIcon />
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="flex h-[24px] w-[24px] items-center justify-center rounded-[6px] hover:bg-[#f5f5f5]"
                                  >
                                    <CloseIcon />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => {
                                      setIsEditingName(true);
                                      setEditingName(
                                        viewingConfig.name || "",
                                      );
                                    }}
                                    className="flex h-[24px] w-[24px] items-center justify-center rounded-[6px] text-[#888] hover:bg-[#f5f5f5]"
                                  >
                                    <EditIcon />
                                  </button>
                                  <button
                                    onClick={() => {
                                      onDeleteConfig(viewingConfig._id);
                                      setOpen(false);
                                    }}
                                    className="flex h-[24px] w-[24px] items-center justify-center rounded-[6px] text-[#FB5F66] hover:bg-[#FFF0F1]"
                                  >
                                    <TrashIcon />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div> */}

                            {/* Model list */}
                            <div
                              ref={modelListRef}
                              className="grey-scrollbar min-h-0 flex-1 overflow-auto p-[5px] pb-[62px]"
                            >
                              {activeProvider === "default-auto" ? (
                                /* Auto Select mode cards (Full Panel for Default Auto Tab) */
                                <div className="flex flex-col gap-[10px]">
                                  {AUTO_MODE_OPTIONS.map((mode) => {
                                    const isAutoModelSelected =
                                      botData?.aiBotConfig?.model === "auto" ||
                                      botData?.botModel === "auto" ||
                                      (!isCustomModel &&
                                        selectedModel?.name === "auto");
                                    const currentMode =
                                      botData?.aiBotConfig?.maestroFlowConfig
                                        ?.selectedMode ||
                                      botData?.maestroFlowConfig
                                        ?.selectedMode ||
                                      "balanced";
                                    const isActive =
                                      isAutoModelSelected &&
                                      normalizeAutoMode(currentMode) ===
                                        mode.id;
                                    const Icon = mode.icon;

                                    return (
                                      <button
                                        key={mode.id}
                                        onClick={() => {
                                          if (onMaestroModeChange) {
                                            onMaestroModeChange(mode.id);
                                          } else {
                                            handleModelSelect(AUTO_MODEL);
                                          }
                                          if (closeOnSelect) setOpen(false);
                                        }}
                                        className={`flex w-full items-center gap-[12px] rounded-[12px] border p-[14px] text-left transition-all ${
                                          isActive
                                            ? "border-[#FB5F66] bg-[#FB5F6608] shadow-[0_2px_8px_rgba(251,95,102,0.1)]"
                                            : "border-[rgba(0,0,0,0.06)] bg-white hover:border-[#FB5F6640] hover:bg-[#FB5F6604]"
                                        } ${fromOutgrow ? (isActive ? "unified-outgrow-active" : "unified-outgrow-hover") : ""}`}
                                      >
                                        <div className="flex flex-1 items-center gap-[8px]">
                                          <Icon />
                                          <p
                                            className={`text-[13px] font-semibold ${isActive ? "text-[#FB5F66]" : "text-[#333]"}`}
                                          >
                                            {mode.label}
                                          </p>
                                        </div>
                                        {isActive && (
                                          <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#FB5F66]">
                                            <svg
                                              width="10"
                                              height="8"
                                              viewBox="0 0 10 8"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M1 4L3.5 6.5L9 1"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : displayModels.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-[8px] py-[24px] text-center">
                                  <p className="text-[11px] text-[#aaa]">
                                    No models found.
                                  </p>
                                </div>
                              ) : (
                                /* ── Unified model rows (BYOK + default) ──── */
                                <>
                                  {renderModels.map((model, idx) => {
                                    const isSel = getIsSelected(model);
                                    const categoriesToRender =
                                      hideCategories ||
                                      hideApiKeyOption ||
                                      hideModelQualities
                                        ? []
                                        : model.categories || [];
                                    const isAllowed =
                                      plan?.name === "Trial" ||
                                      plan?.name === "Basic" ||
                                      plan?.name === "Professional" ||
                                      plan?.name === "outgrow" ||
                                      plan?.name === "Lifetime" ||
                                      (model.autoMode &&
                                        allowedModels?.some(
                                          (am) => normalize(am) === "auto",
                                        )) ||
                                      allowedModels?.some((am) => {
                                        const nm = normalize(model.name);
                                        const na = normalize(am);
                                        return (
                                          nm === na ||
                                          model.name.toLowerCase() ===
                                            am.toLowerCase()
                                        );
                                      });
                                    const hasActiveCustomKey =
                                      isCustomView && !!viewingConfig;
                                    const isDisabled =
                                      !byok &&
                                      !hasActiveCustomKey &&
                                      !isAllowed;
                                    const isEffectivelySelected =
                                      isSel && !isDisabled;

                                    return (
                                      <div key={model.name + idx}>
                                        <button
                                          disabled={isDisabled}
                                          onMouseEnter={() =>
                                            !isCustomView &&
                                            setHoveredModel(model)
                                          }
                                          onMouseLeave={() =>
                                            !isCustomView &&
                                            setHoveredModel(null)
                                          }
                                          onClick={() => {
                                            if (isDisabled) return;
                                            handleClick(model);
                                          }}
                                          className={`group mb-[4px] flex w-full items-center justify-between gap-[8px] rounded-[8px] border border-transparent px-[10px] py-[8px] text-left transition-colors ${
                                            isEffectivelySelected
                                              ? "border border-[rgba(251,95,102,0.50)] shadow-[0_0_5px_0_rgba(251,95,102,0.90)] transition-all duration-300"
                                              : isDisabled
                                                ? "cursor-not-allowed opacity-40"
                                                : "hover:bg-[#f5f5f5]"
                                          } ${fromOutgrow ? (isEffectivelySelected ? "unified-outgrow-active" : isDisabled ? "" : "unified-outgrow-hover") : ""}`}
                                        >
                                          <div className="flex min-w-0 flex-1 items-center gap-[4px] overflow-hidden">
                                            <img
                                              src={model.url}
                                              alt=""
                                              className="h-[15px] w-[15px] flex-shrink-0 rounded-sm"
                                            />
                                            <MarqueeName
                                              text={model.displayName}
                                              className={`text-[12px] font-medium ${isEffectivelySelected ? "text-[#333]" : "text-[#333]"}`}
                                            />
                                          </div>
                                          <div
                                            className={`flex shrink-0 items-center gap-[4px] transition-opacity ${isSel ? "opacity-100" : "opacity-[0.3] group-hover:opacity-100"}`}
                                          >
                                            {categoriesToRender.map((c, i) => {
                                              const Icon = model.autoMode
                                                ? AUTO_MODE_CATEGORY_ICON_BY_LABEL[
                                                    c
                                                  ]
                                                : categoryIcons[c];
                                              return (
                                                <span
                                                  key={i + model.name}
                                                  className="flex shrink-0 items-center gap-[3px] whitespace-nowrap rounded-full border border-[rgba(0,0,0,0.10)] bg-white px-[7px] py-[3px] text-[10px] text-[#666] group-hover:border-[rgba(251,95,102,0.30)]"
                                                >
                                                  {Icon && <Icon />} {c}
                                                </span>
                                              );
                                            })}
                                            {isDisabled && (
                                              <span className="flex-shrink-0 opacity-60">
                                                <LockIcon />
                                              </span>
                                            )}
                                          </div>
                                        </button>
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                            {isCustomView &&
                              viewingConfig &&
                              isOpenAICompatibleProvider(
                                viewingConfig.provider,
                              ) &&
                              onAddModelToConfig && (
                                <div
                                  className={`absolute bottom-0 left-0 right-0 z-10 flex flex-shrink-0 justify-center px-[10px] py-[8px] ${isModelListScrollable ? "bg-gradient-to-t from-white via-white/95 to-transparent pt-[34px]" : "bg-white"}`}
                                >
                                  <button
                                    className="flex w-full max-w-[186px] items-center justify-center gap-[4px] rounded-[8px] border border-[rgba(0,0,0,0.04)] bg-[#f9f9f9] px-[10px] py-[8px] text-[12px] font-normal text-[#FB5F66] shadow-[0_1px_10px_0_rgba(0,0,0,0.05)] transition-opacity hover:opacity-80"
                                    onClick={() => {
                                      onAddModelToConfig(viewingConfig);
                                      setOpen(false);
                                    }}
                                  >
                                    <PlusIcon /> Add model
                                  </button>
                                </div>
                              )}
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
                {showByokCta && (
                  <div className="byok_togglebar mt-[10px] flex items-center justify-between rounded-[10px] p-[15px]">
                    <div className="flex items-center gap-3">
                      <div className="border-[rgba(0, 0, 0, 0.05)] flex h-8 w-8 items-center justify-center rounded-[10px] border bg-[#FFF] text-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M13.5336 2.46168C12.7394 1.66638 11.7004 1.16185 10.5842 1.02953C9.46808 0.897207 8.33991 1.1448 7.38177 1.73236C6.42362 2.31991 5.69138 3.21316 5.30321 4.26796C4.91504 5.32275 4.89359 6.47757 5.24231 7.54606L1.79105 10.9973C1.69779 11.0898 1.62385 11.2 1.57353 11.3213C1.52321 11.4427 1.49751 11.5728 1.49793 11.7042V13.4973C1.49793 13.7625 1.60329 14.0169 1.79082 14.2044C1.97836 14.392 2.23271 14.4973 2.49793 14.4973H4.49793C4.63054 14.4973 4.75772 14.4446 4.85148 14.3509C4.94525 14.2571 4.99793 14.1299 4.99793 13.9973V12.9973H5.99793C6.13054 12.9973 6.25772 12.9446 6.35148 12.8509C6.44525 12.7571 6.49793 12.6299 6.49793 12.4973V11.4973H7.49793C7.56361 11.4974 7.62866 11.4845 7.68936 11.4594C7.75005 11.4343 7.80521 11.3975 7.85168 11.3511L8.44918 10.7529C8.9493 10.9156 9.47202 10.9981 9.99793 10.9973H10.0042C10.9926 10.9961 11.9585 10.702 12.7798 10.1521C13.6011 9.60223 14.241 8.82127 14.6187 7.90787C14.9964 6.99447 15.0949 5.98962 14.9017 5.02028C14.7086 4.05093 14.2325 3.16057 13.5336 2.46168ZM11.2479 5.74731C11.0501 5.74731 10.8568 5.68866 10.6924 5.57878C10.5279 5.4689 10.3997 5.31272 10.3241 5.12999C10.2484 4.94727 10.2286 4.7462 10.2671 4.55222C10.3057 4.35824 10.401 4.18006 10.5408 4.0402C10.6807 3.90035 10.8589 3.80511 11.0528 3.76652C11.2468 3.72794 11.4479 3.74774 11.6306 3.82343C11.8133 3.89912 11.9695 4.02729 12.0794 4.19174C12.1893 4.35619 12.2479 4.54953 12.2479 4.74731C12.2479 5.01253 12.1426 5.26688 11.955 5.45442C11.7675 5.64195 11.5131 5.74731 11.2479 5.74731Z"
                            fill="url(#paint0_linear_7178_24221)"
                          />
                          <defs>
                            <linearGradient
                              id="paint0_linear_7178_24221"
                              x1="1.49792"
                              y1="7.74603"
                              x2="14.9981"
                              y2="7.74603"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor="#FF787E" />
                              <stop offset="1" stopColor="#E3232C" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2">
                        <p className="text-[12px] font-semibold text-[#444]">
                          Enable Bring Your Own Key (BYOK)
                        </p>
                        <p className="text-[12px] text-[#444]"> to access</p>
                        <p className="text-[12px] font-bold text-[#FB5F66]">
                          5x more credits
                        </p>
                        <p className="text-[12px] text-[#444]">
                          and scale your usage without limits.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onTryNow}
                      className="group flex items-center gap-1.5 whitespace-nowrap text-[13px] font-bold text-[#FB5F66] transition-opacity hover:opacity-80"
                    >
                      <SparklesIcon />
                      Try Now
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default UnifiedModelSelector;
